import type { JobEvent } from "@webyonetim/contracts";
import { useEffect, useRef, useState } from "react";

import { API_BASE, apiRequest } from "./api";

export interface JobStreamState {
  readonly connected: boolean;
  readonly cursor: string | undefined;
  readonly events: readonly JobEvent[];
  readonly lastUpdatedAt: string | undefined;
  readonly reconnecting: boolean;
}

export function appendUniqueJobEvent(
  events: readonly JobEvent[],
  incoming: JobEvent,
): readonly JobEvent[] {
  if (events.some((event) => event.id === incoming.id)) return events;
  return [...events, incoming].sort((left, right) => left.sequence - right.sequence);
}

function isJobEvent(value: unknown): value is JobEvent {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value as Readonly<Record<string, unknown>>;
  return (
    typeof candidate["id"] === "string" &&
    typeof candidate["event"] === "string" &&
    typeof candidate["sequence"] === "number" &&
    Number.isInteger(candidate["sequence"]) &&
    typeof candidate["emittedAt"] === "string" &&
    typeof candidate["jobId"] === "string" &&
    typeof candidate["operationId"] === "string" &&
    typeof candidate["correlationId"] === "string" &&
    candidate["data"] !== null &&
    typeof candidate["data"] === "object" &&
    !Array.isArray(candidate["data"])
  );
}

export function useJobEvents(jobId: string | undefined): JobStreamState {
  const [state, setState] = useState<JobStreamState>({
    connected: false,
    cursor: undefined,
    events: [],
    lastUpdatedAt: undefined,
    reconnecting: false,
  });
  const seen = useRef(new Set<string>());
  useEffect(() => {
    if (jobId === undefined) return undefined;
    let active = true;
    if (typeof EventSource === "undefined") {
      const poll = window.setInterval(() => {
        void apiRequest<{ readonly events?: readonly JobEvent[] }>(
          `/jobs/${encodeURIComponent(jobId)}`,
        )
          .then((result) => {
            if (!active || result.events === undefined) return;
            const events = result.events;
            setState((current) => ({
              ...current,
              connected: false,
              events: events.reduce(appendUniqueJobEvent, current.events),
              lastUpdatedAt: new Date().toISOString(),
              reconnecting: false,
            }));
          })
          .catch(() => {
            setState((current) => ({ ...current, connected: false, reconnecting: true }));
          });
      }, 5000);
      return () => {
        active = false;
        window.clearInterval(poll);
      };
    }
    const source = new EventSource(`${API_BASE}/jobs/${encodeURIComponent(jobId)}/events`, {
      withCredentials: true,
    });
    source.onopen = () => {
      if (active) setState((current) => ({ ...current, connected: true, reconnecting: false }));
    };
    source.onerror = () => {
      if (active) setState((current) => ({ ...current, connected: false, reconnecting: true }));
    };
    source.onmessage = (message) => {
      if (!active || seen.current.has(message.lastEventId)) return;
      try {
        const frameData: unknown = message.data;
        if (typeof frameData !== "string") return;
        const parsed: unknown = JSON.parse(frameData);
        if (!isJobEvent(parsed)) return;
        const event = parsed;
        seen.current.add(event.id);
        setState((current) => ({
          connected: true,
          cursor: event.id,
          events: appendUniqueJobEvent(current.events, event),
          lastUpdatedAt: event.emittedAt,
          reconnecting: false,
        }));
      } catch {
        // Ignore malformed untrusted stream frames; the server snapshot remains authoritative.
      }
    };
    return () => {
      active = false;
      source.close();
      seen.current.clear();
    };
  }, [jobId]);
  return state;
}
